import {
  AnyCalendarDate,
  AnyDateTime,
  CalendarDate,
  DateValue,
  ZonedDateTime,
  now as ariaNow,
  parseZonedDateTime as ariaParseZonedDateTime,
  today as ariaToday,
  toTimeZone,
} from '@internationalized/date';
import { maybe } from '@rematter/ui-web';

import { store } from '../state';
import { $localization } from '../state/preferences.state';

export const parseZonedDateTime = (dateTime: string): ZonedDateTime => {
  const parsedDateTime = ariaParseZonedDateTime(dateTime);
  const localTimezone = store.get($localization).timezone ?? 'UTC';

  return toTimeZone(parsedDateTime, localTimezone);
};

export const maybeParseZonedDateTime = (dateTime?: string | null) =>
  maybe(dateTime, parseZonedDateTime);

export const parseCalendarDate = (date: string): CalendarDate => {
  const nativeDate = new Date(date.substring(0, 10) + 'T00:00:00');
  return new CalendarDate(
    nativeDate.getFullYear(),
    nativeDate.getMonth() + 1,
    nativeDate.getDate(),
  );
};

export const maybeParseCalendarDate = (date?: string | null) =>
  maybe(date, parseCalendarDate);

export const parseCalendarDateOrNull = (
  date?: string | null,
): CalendarDate | null =>
  date && typeof date === 'string' ? parseCalendarDate(date) : null;

export const toCalendarDateOrNull = (
  date?: DateValue | string | null | undefined,
): CalendarDate | null => {
  if (!date) return null;
  if (typeof date === 'string') return parseCalendarDate(date);
  if (
    typeof date === 'object' &&
    date &&
    'year' in date &&
    'month' in date &&
    'day' in date
  ) {
    return new CalendarDate(date.year, date.month, date.day);
  }
  return null;
};

// Handle AnyCalendarDate which can be CalendarDate, CalendarDateTime, or ZonedDateTime
export const anyCalendarDateToCalendarDate = (
  date?: AnyCalendarDate | AnyDateTime,
): CalendarDate | null => {
  if (!date) return null;
  if (typeof date === 'string') return parseCalendarDate(date);
  if (
    typeof date === 'object' &&
    date &&
    'year' in date &&
    'month' in date &&
    'day' in date
  ) {
    return new CalendarDate(date.year, date.month, date.day);
  }
  return null;
};

export const seconds = (seconds: number) => seconds * 1000;
export const minutes = (minutes: number) => minutes * seconds(60);
export const hours = (hours: number) => hours * minutes(60);
export const days = (days: number) => days * hours(24);
export const weeks = (weeks: number) => weeks * days(7);
export const months = (months: number) => months * days(30);
export const years = (years: number) => years * months(12);

// const parseUTCDate = (createdAt: string) =>
//   new Date(createdAt.substring(0, 19) + 'Z');

export const today = () => {
  const localTimezone = store.get($localization).timezone ?? 'UTC';
  return ariaToday(localTimezone);
};

export const toISODateString = (date: DateValue) =>
  date.toDate('UTC').toISOString().substring(0, 10);

export const anyDateToISOString = (date?: unknown): string | undefined => {
  if (!date) return undefined;
  if (typeof date === 'string') return date.substring(0, 10);
  if (
    typeof date === 'object' &&
    date &&
    'year' in date &&
    'month' in date &&
    'day' in date
  ) {
    const d = date as { year: number; month: number; day: number };
    const calendarDate = new CalendarDate(d.year, d.month, d.day);
    return calendarDate.toDate('UTC').toISOString().substring(0, 10);
  }
  return undefined;
};

export const dateToZonedDateTimeStart = (
  date?: unknown,
): string | undefined => {
  if (!date) return undefined;
  if (
    typeof date === 'object' &&
    date &&
    'year' in date &&
    'month' in date &&
    'day' in date
  ) {
    const d = date as { year: number; month: number; day: number };
    const calendarDate = new CalendarDate(d.year, d.month, d.day);
    return calendarDate.toDate('UTC').toISOString();
  }
  return undefined;
};

export const dateStringToUTCStart = (
  date?: string | null,
): string | undefined => {
  if (!date) return undefined;
  const parsed = new Date(`${date.substring(0, 10)}T00:00:00Z`);
  if (isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
};

export const dateToZonedDateTimeEnd = (date?: unknown): string | undefined => {
  if (!date) return undefined;
  if (
    typeof date === 'object' &&
    date &&
    'year' in date &&
    'month' in date &&
    'day' in date
  ) {
    const d = date as { year: number; month: number; day: number };
    const calendarDate = new CalendarDate(d.year, d.month, d.day);
    const endOfDay = calendarDate.toDate('UTC');
    endOfDay.setUTCHours(23, 59, 59, 999);
    return endOfDay.toISOString();
  }
  return undefined;
};

export const now = () => {
  const localTimezone = store.get($localization).timezone ?? 'UTC';
  return ariaNow(localTimezone);
};

/**
 * Formats a ZonedDateTime string (e.g., "2026-01-22T08:00:00+00:00[UTC]") for display.
 * Returns a localized date string or '—' if invalid.
 * Set includeTime to true to include time in the output.
 */
export const formatZonedDateTime = (
  dateStr?: string | null,
  options?: Intl.DateTimeFormatOptions & { includeTime?: boolean },
): string => {
  if (!dateStr) return '—';
  try {
    const parsed = parseZonedDateTime(dateStr);
    const { includeTime, ...formatOptions } = options ?? {};
    if (includeTime) {
      return parsed.toDate().toLocaleString(undefined, formatOptions);
    }
    return parsed.toDate().toLocaleDateString(undefined, formatOptions);
  } catch {
    return '—';
  }
};

/**
 * Formats a ZonedDateTime string as a short date (e.g., "Jan 22, 2026").
 */
export const formatZonedDateTimeShort = (dateStr?: string | null): string =>
  formatZonedDateTime(dateStr, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

export const formatExclusiveEndDateShort = (
  dateStr?: string | null,
): string => {
  if (!dateStr) return '—';
  try {
    const parsed = parseZonedDateTime(dateStr).subtract({ days: 1 });
    return parsed.toDate().toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
};
